import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';

const PREFIJOS = ['SRV', 'REP', 'MAT', 'INS'];

const PREFIJO_LABEL = {
  SRV: 'Servicio',
  REP: 'Repuesto',
  MAT: 'Material',
  INS: 'Insumo',
};

const INPUT_ST = {
  padding: '8px 12px', border: '1.5px solid #2E4A40', borderRadius: 8,
  fontSize: 14, background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
};

const LABEL_ST = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#9FAAA5', marginBottom: 5,
};

function highlight(text, q) {
  if (!q || !text) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: '#2D2200', color: '#F59E0B', padding: '0 2px', borderRadius: 2 }}>
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

const CLP = (n) =>
  Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const FORM_VACIO = {
  prefijo: 'SRV', nombre: '', descripcion: '', unidad: 'unidad',
  hh_incluidas: '', precio_materiales: '', precio_ref: '',
};

export default function CatalogoItems() {
  const [items, setItems]               = useState([]);
  const [q, setQ]                       = useState('');
  const [filtroPrefijo, setFiltroPrefijo] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('true');
  const [loading, setLoading]           = useState(false);
  const [modal, setModal]               = useState(null);
  const [editId, setEditId]             = useState(null);
  const [form, setForm]                 = useState(FORM_VACIO);
  const [valorHH, setValorHH]           = useState(25000);
  const [guardando, setGuardando]       = useState(false);
  const [error, setError]               = useState('');
  const [descargando, setDescargando]   = useState(false);
  const [importar, setImportar]         = useState(false);

  useEffect(() => {
    api.get('/catalogo/configuracion').then(r => {
      const cfg = r.data.find(c => c.clave === 'valor_hh');
      if (cfg) setValorHH(parseFloat(cfg.valor));
    }).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (filtroPrefijo) params.prefijo = filtroPrefijo;
      if (filtroActivo !== '') params.activo = filtroActivo;
      const r = await api.get('/catalogo', { params });
      setItems(r.data);
    } catch {
      setError('Error al cargar catálogo');
    } finally {
      setLoading(false);
    }
  }, [q, filtroPrefijo, filtroActivo]);

  useEffect(() => { cargar(); }, [cargar]);

  const precioCalculado =
    (parseFloat(form.precio_materiales) || 0) +
    (parseFloat(form.hh_incluidas) || 0) * valorHH;

  const abrirNuevo = () => { setForm(FORM_VACIO); setEditId(null); setError(''); setModal('nuevo'); };

  const abrirEditar = (item) => {
    setForm({
      prefijo: item.prefijo || 'SRV',
      nombre: item.nombre || '',
      descripcion: item.descripcion || '',
      unidad: item.unidad || 'unidad',
      hh_incluidas: item.hh_incluidas ?? '',
      precio_materiales: item.precio_materiales ?? '',
      precio_ref: item.precio_ref ?? '',
    });
    setEditId(item.id); setError(''); setModal('editar');
  };

  const cerrar = () => { setModal(null); setEditId(null); setError(''); };

  const guardar = async () => {
    if (!form.nombre.trim()) { setError('El nombre es requerido'); return; }
    setGuardando(true); setError('');
    try {
      const payload = {
        prefijo: form.prefijo,
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
        unidad: form.unidad || 'unidad',
        hh_incluidas: parseFloat(form.hh_incluidas) || 0,
        precio_materiales: parseFloat(form.precio_materiales) || 0,
        precio_ref: form.precio_ref !== '' ? parseFloat(form.precio_ref) : undefined,
      };
      if (modal === 'nuevo') await api.post('/catalogo', payload);
      else await api.put(`/catalogo/${editId}`, payload);
      cerrar(); cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const desactivar = async (id, activo) => {
    const accion = activo ? 'desactivar' : 'activar';
    if (!window.confirm(`¿${accion.charAt(0).toUpperCase() + accion.slice(1)} este ítem?`)) return;
    try {
      if (activo) await api.delete(`/catalogo/${id}`);
      else await api.put(`/catalogo/${id}`, { activo: true });
      cargar();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const descargarExcel = async () => {
    setDescargando(true);
    try {
      const r = await api.get('/catalogo/export', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `catalogo_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('No se pudo descargar el Excel');
    } finally {
      setDescargando(false);
    }
  };

  const setF = (k) => (v) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      if (k === 'hh_incluidas' || k === 'precio_materiales') next.precio_ref = '';
      return next;
    });
  };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Buscar código o nombre…"
          style={{ ...INPUT_ST, flex: 1, minWidth: 180 }} />
        <select value={filtroPrefijo} onChange={e => setFiltroPrefijo(e.target.value)}
          style={INPUT_ST}>
          <option value="">Todos los tipos</option>
          {PREFIJOS.map(p => <option key={p} value={p}>{PREFIJO_LABEL[p]}</option>)}
        </select>
        <select value={filtroActivo} onChange={e => setFiltroActivo(e.target.value)}
          style={INPUT_ST}>
          <option value="true">Activos</option>
          <option value="false">Inactivos</option>
          <option value="">Todos</option>
        </select>
        <button onClick={descargarExcel} disabled={descargando} style={{
          padding: '8px 14px', background: 'transparent', color: '#4CAF50',
          border: '1.5px solid rgba(79,175,80,0.4)', borderRadius: 8, fontWeight: 600,
          cursor: descargando ? 'wait' : 'pointer', fontSize: 14,
        }}>{descargando ? 'Generando…' : '⬇ Excel'}</button>
        <button onClick={() => setImportar(true)} style={{
          padding: '8px 14px', background: 'transparent', color: '#38BDF8',
          border: '1.5px solid rgba(56,189,248,0.4)', borderRadius: 8, fontWeight: 600,
          cursor: 'pointer', fontSize: 14,
        }}>⬆ Importar</button>
        <button onClick={abrirNuevo} style={{
          padding: '8px 18px', background: '#0f6e56', color: 'white',
          border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
        }}>+ Nuevo ítem</button>
      </div>

      {/* Tabla */}
      {loading ? (
        <p style={{ color: '#8A9994', textAlign: 'center', padding: 24 }}>Cargando…</p>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#161C19' }}>
                {['Código', 'Tipo', 'Nombre', 'Unidad', 'HH', 'Materiales', 'Precio ref.', 'Estado', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 12px', textAlign: 'left', fontWeight: 600,
                    color: '#9FAAA5', borderBottom: '1px solid rgba(255,255,255,0.08)',
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, color: '#6B7C74', textAlign: 'center' }}>
                  Sin resultados
                </td></tr>
              )}
              {items.map((item, i) => (
                <tr key={item.id} style={{
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: i % 2 === 0 ? '#111714' : '#0D1311',
                  opacity: item.activo ? 1 : 0.45,
                }}>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontWeight: 600, color: '#4CAF50' }}>
                    {highlight(item.codigo, q)}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ background: '#12382D', color: '#4CAF50', borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>
                      {PREFIJO_LABEL[item.prefijo] || item.prefijo}
                    </span>
                  </td>
                  <td style={{ padding: '9px 12px', color: '#F4F7F5' }}>{highlight(item.nombre, q)}</td>
                  <td style={{ padding: '9px 12px', color: '#8A9994' }}>{item.unidad}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: '#C9D3CE' }}>{item.hh_incluidas}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: '#C9D3CE' }}>{CLP(item.precio_materiales)}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 700, color: '#F4F7F5' }}>{CLP(item.precio_ref)}</td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{
                      background: item.activo ? '#12382D' : '#2D0F0F',
                      color: item.activo ? '#4CAF50' : '#EF4444',
                      borderRadius: 4, padding: '2px 8px', fontSize: 12,
                    }}>{item.activo ? 'Activo' : 'Inactivo'}</span>
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => abrirEditar(item)} style={{
                        padding: '4px 10px', border: '1px solid rgba(79,175,80,0.3)',
                        borderRadius: 6, background: '#12382D', color: '#4CAF50',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      }}>Editar</button>
                      <button onClick={() => desactivar(item.id, item.activo)} style={{
                        padding: '4px 10px',
                        border: `1px solid ${item.activo ? 'rgba(198,40,40,0.3)' : 'rgba(79,175,80,0.3)'}`,
                        borderRadius: 6,
                        background: item.activo ? '#2D0F0F' : '#12382D',
                        color: item.activo ? '#EF4444' : '#4CAF50',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      }}>{item.activo ? 'Desactivar' : 'Activar'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal nuevo/editar */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 1000, padding: 16 }}>
          <div style={{ background: '#111714', borderRadius: 16, padding: 28, border: '1px solid rgba(255,255,255,0.08)',
                        width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#F4F7F5' }}>
                {modal === 'nuevo' ? 'Nuevo ítem' : 'Editar ítem'}
              </h2>
              <button onClick={cerrar} style={{ background: 'none', border: 'none',
                fontSize: 22, cursor: 'pointer', color: '#8A9994' }}>×</button>
            </div>

            {error && (
              <div style={{ background: '#2D0F0F', color: '#EF4444', borderRadius: 8,
                            padding: '10px 14px', marginBottom: 14, fontSize: 13,
                            border: '1px solid rgba(198,40,40,0.3)' }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={LABEL_ST}>
                Tipo {modal === 'editar' && <span style={{ fontWeight: 400, color: '#6B7C74' }}>(no modificable)</span>}
              </label>
              {modal === 'editar' ? (
                <div style={{ padding: '10px 12px', background: '#161C19', borderRadius: 8,
                              fontSize: 14, color: '#8A9994', border: '1.5px solid #2E4A40' }}>
                  {PREFIJO_LABEL[form.prefijo]} ({form.prefijo})
                </div>
              ) : (
                <select value={form.prefijo} onChange={e => setF('prefijo')(e.target.value)}
                  style={{ ...INPUT_ST, width: '100%' }}>
                  {PREFIJOS.map(p => <option key={p} value={p}>{PREFIJO_LABEL[p]} ({p})</option>)}
                </select>
              )}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={LABEL_ST}>Nombre *</label>
              <input value={form.nombre} onChange={e => setF('nombre')(e.target.value)}
                placeholder="Ej: Revisión sistema de frenos"
                style={{ ...INPUT_ST, width: '100%', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={LABEL_ST}>Descripción</label>
              <textarea value={form.descripcion} onChange={e => setF('descripcion')(e.target.value)}
                rows={2} placeholder="Detalle opcional…"
                style={{ ...INPUT_ST, width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={LABEL_ST}>Unidad</label>
              <input value={form.unidad} onChange={e => setF('unidad')(e.target.value)}
                placeholder="unidad, hora, m², kg…"
                style={{ ...INPUT_ST, width: '100%', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={LABEL_ST}>Horas-hombre</label>
                <input type="number" min="0" step="0.5" value={form.hh_incluidas}
                  onChange={e => setF('hh_incluidas')(e.target.value)}
                  style={{ ...INPUT_ST, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={LABEL_ST}>Materiales (CLP)</label>
                <input type="number" min="0" step="100" value={form.precio_materiales}
                  onChange={e => setF('precio_materiales')(e.target.value)}
                  style={{ ...INPUT_ST, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ background: '#161C19', borderRadius: 10, padding: '12px 14px', marginBottom: 14,
                          border: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ fontSize: 13, color: '#9FAAA5', marginBottom: 6 }}>
                Precio calculado: <strong style={{ color: '#F4F7F5' }}>{CLP(precioCalculado)}</strong>
                <span style={{ fontSize: 11, marginLeft: 8, color: '#6B7C74' }}>
                  (materiales + {parseFloat(form.hh_incluidas) || 0} HH × {CLP(valorHH)})
                </span>
              </div>
              <label style={LABEL_ST}>Precio final (editable)</label>
              <input type="number" min="0" step="100"
                value={form.precio_ref !== '' ? form.precio_ref : Math.round(precioCalculado)}
                onChange={e => setF('precio_ref')(e.target.value)}
                style={{ ...INPUT_ST, width: '100%', boxSizing: 'border-box', borderColor: '#0f6e56' }} />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={cerrar} style={{
                padding: '9px 20px', border: '1.5px solid rgba(255,255,255,0.12)',
                borderRadius: 8, background: 'transparent', cursor: 'pointer',
                fontSize: 14, color: '#9FAAA5',
              }}>Cancelar</button>
              <button onClick={guardar} disabled={guardando} style={{
                padding: '9px 20px', background: '#0f6e56', color: 'white',
                border: 'none', borderRadius: 8, fontWeight: 600,
                cursor: guardando ? 'wait' : 'pointer', fontSize: 14,
              }}>{guardando ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {importar && (
        <ImportarCatalogoModal
          onClose={() => setImportar(false)}
          onDone={() => { setImportar(false); cargar(); }}
        />
      )}
    </div>
  );
}

const CAMPO_LABEL = {
  nombre: 'Nombre', descripcion: 'Descripción', unidad: 'Unidad',
  hh_incluidas: 'HH', precio_materiales: 'Materiales', precio_ref: 'Precio ref.', activo: 'Activo',
};

function fmtVal(campo, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (campo === 'activo') return v ? 'Activo' : 'Inactivo';
  if (['precio_materiales', 'precio_ref'].includes(campo)) return CLP(v);
  return String(v);
}

function ImportarCatalogoModal({ onClose, onDone }) {
  const [archivo, setArchivo]     = useState(null);
  const [preview, setPreview]     = useState(null);
  const [resultado, setResultado] = useState(null);
  const [err, setErr]             = useState('');
  const [cargando, setCargando]   = useState(false);
  const fileRef = useRef(null);

  const analizar = async (file) => {
    setErr(''); setResultado(null); setPreview(null);
    const fd = new FormData(); fd.append('archivo', file);
    setCargando(true);
    try {
      const r = await api.post('/catalogo/importar/preview', fd);
      setPreview(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Error al analizar el archivo'); }
    finally { setCargando(false); }
  };

  const confirmar = async () => {
    if (!archivo) return;
    const fd = new FormData(); fd.append('archivo', archivo);
    setCargando(true);
    try {
      const r = await api.post('/catalogo/importar/confirmar', fd);
      setResultado(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Error al importar'); }
    finally { setCargando(false); }
  };

  const hayCambios = preview && (preview.crear > 0 || preview.actualizar > 0);
  const colorAccion = { crear: '#4CAF50', actualizar: '#E0A415', sin_cambios: '#6B7C74', error: '#EF4444' };
  const labelAccion = { crear: '+ Crear', actualizar: '↻ Actualizar', sin_cambios: '= Sin cambios', error: '⚠ Error' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#111714', borderRadius: 16, padding: 28, border: '1px solid rgba(255,255,255,0.08)',
                    width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#F4F7F5' }}>Importar catálogo desde Excel</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#8A9994' }}>×</button>
        </div>

        {!resultado && (
          <p style={{ color: '#8A9994', fontSize: 13, marginTop: 0 }}>
            Descarga el Excel con <b>⬇ Excel</b>, edítalo y súbelo aquí. Se detectan los cambios y
            se muestran <b>antes de aplicar</b>. Las filas se identifican por <b>ID</b> (o Código); las que no
            existan se crean con un código nuevo según su Tipo. Deja <b>Precio referencia</b> vacío para
            recalcularlo (materiales + HH × valor HH).
          </p>
        )}

        {!resultado && (
          <div style={{ marginBottom: 16 }}>
            <button onClick={() => fileRef.current?.click()} style={{
              padding: '9px 18px', background: '#0f6e56', color: 'white', border: 'none',
              borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
            }}>⬆ Seleccionar archivo</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
              onChange={e => { const file = e.target.files?.[0]; if (file) { setArchivo(file); analizar(file); } }} />
            {archivo && <span style={{ color: '#C9D3CE', fontSize: 13, marginLeft: 12 }}>{archivo.name}</span>}
          </div>
        )}

        {err && <div style={{ background: '#2D0F0F', color: '#EF4444', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{err}</div>}
        {cargando && <p style={{ color: '#8A9994' }}>Procesando…</p>}

        {preview && !resultado && (
          <div style={{ background: '#161C19', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16 }}>
            <p style={{ margin: '0 0 10px', color: '#F4F7F5', fontSize: 14 }}>
              {preview.total} fila(s): <b style={{ color: '#4CAF50' }}>{preview.crear} nuevas</b>,{' '}
              <b style={{ color: '#E0A415' }}>{preview.actualizar} a actualizar</b>,{' '}
              <span style={{ color: '#6B7C74' }}>{preview.sin_cambios} sin cambios</span>
              {preview.errores ? <>, <b style={{ color: '#EF4444' }}>{preview.errores} con error</b></> : null}.
            </p>

            <div style={{ maxHeight: 300, overflowY: 'auto', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {preview.detalle?.map((d, i) => (
                <div key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ color: colorAccion[d.accion], fontWeight: 700 }}>{labelAccion[d.accion]}</span>
                    <span style={{ color: '#8A9994', fontFamily: 'monospace' }}>{d.codigo}</span>
                    <span style={{ color: '#F4F7F5' }}>{d.nombre}</span>
                    {d.accion === 'error' && <span style={{ color: '#EF4444' }}>— {d.motivo}</span>}
                  </div>
                  {d.accion === 'actualizar' && d.cambios?.length > 0 && (
                    <div style={{ marginTop: 4, marginLeft: 16, color: '#C9D3CE', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {d.cambios.map((c, j) => (
                        <div key={j} style={{ fontSize: 12 }}>
                          <span style={{ color: '#9FAAA5' }}>{CAMPO_LABEL[c.campo] || c.campo}:</span>{' '}
                          <span style={{ color: '#EF4444', textDecoration: 'line-through' }}>{fmtVal(c.campo, c.antes)}</span>{' '}
                          <span style={{ color: '#6B7C74' }}>→</span>{' '}
                          <span style={{ color: '#4CAF50' }}>{fmtVal(c.campo, c.despues)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} style={{
                padding: '9px 20px', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 8,
                background: 'transparent', cursor: 'pointer', fontSize: 14, color: '#9FAAA5',
              }}>Cancelar</button>
              <button onClick={confirmar} disabled={cargando || !hayCambios} style={{
                padding: '9px 20px', background: (cargando || !hayCambios) ? '#0a4a38' : '#0f6e56',
                color: 'white', border: 'none', borderRadius: 8, fontWeight: 600,
                cursor: (cargando || !hayCambios) ? 'not-allowed' : 'pointer', fontSize: 14,
                opacity: hayCambios ? 1 : 0.6,
              }}>{cargando ? 'Aplicando…' : `Confirmar (${preview.crear + preview.actualizar})`}</button>
            </div>
          </div>
        )}

        {resultado && (
          <div style={{ background: '#12382D', color: '#4CAF50', padding: '14px 16px', borderRadius: 12 }}>
            {resultado.message}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onDone} style={{
                padding: '9px 20px', background: '#0f6e56', color: 'white', border: 'none',
                borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14,
              }}>Ver catálogo</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
